import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, inject } from '@angular/core';
import { DomSanitizer, Title } from '@angular/platform-browser';
import { Params, Router } from '@angular/router';
import { BehaviorSubject, distinctUntilChanged, map, Observable, of, switchMap, take } from 'rxjs';
import { AiChatLayoutService } from '@panaya/ai-chat/features';

import { environment } from '../environments/environment';
import { RouterEventsFacade, RouterStoreService } from '@frontend-site/shared/router-store';
import { init, PendoService, ProjectStore, SessionRepository } from '@frontend-site/shared/core';
import {
    HybridCommunicationService,
    HybridIframeManagerService,
    HybridMessagePayloadService,
    registerHybridIframePositioningChangeDetection
} from '@frontend-site/shared/hybrid';
import { TranslateService } from '@ngx-translate/core';
import { PersistenceFacade } from '@frontend-site/persistence';
import { PTreeObservableService } from '@modules/tree-actions/services/p-tree-observable.service';
import { RecommendationService } from '@frontend-site/recommendations/features';

interface AppViewModel {
    isLoggedIn: boolean;
    showNewApp: boolean;
}

function createInitialViewModel(): AppViewModel {
    return {
        isLoggedIn: false,
        showNewApp: true
    };
}
function getIframeSrc(): string {
    return environment.iframeSrc + location.pathname.replace('/site/rdx', '') + location.search;
}

@Component({
    selector: 'p-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent {
    @HostBinding('class.draggable-boundary') draggableBoundary = true;

    private vm = createInitialViewModel();
    readonly vm$ = new BehaviorSubject<AppViewModel>(this.vm);
    readonly iframeSrc = this.sanitizer.bypassSecurityTrustResourceUrl(getIframeSrc());

    private readonly layoutService = inject(AiChatLayoutService);

    protected readonly isPanelOpen$ = this.layoutService.isPanelOpen$;
    protected readonly isCanvasOpen$ = this.layoutService.isCanvasOpen$;

    constructor(
        translate: TranslateService,
        titleService: Title,
        routerStoreService: RouterStoreService,
        routerEventsFacade: RouterEventsFacade,
        pendoService: PendoService,
        sessionRepository: SessionRepository,
        persistence: PersistenceFacade,
        router: Router,
        changeDetector: ChangeDetectorRef,
        private readonly hybridCommunicationService: HybridCommunicationService,
        private readonly hybridMessagePayloadService: HybridMessagePayloadService,
        private readonly sanitizer: DomSanitizer,
        private readonly recommendationService: RecommendationService,
        private projectStore: ProjectStore,
        private treeObservableService: PTreeObservableService,
        private readonly hybridIframeManager: HybridIframeManagerService
    ) {
        const routerState$ = routerStoreService.state$;
        routerState$.subscribe((state) => {
            if (state.data?.rememberMe) {
                persistence.rememberPage(state.data?.rememberMe, state.path);
            }
        });

        registerHybridIframePositioningChangeDetection(changeDetector);

        this.listenToAppToggle();

        //important to clear old site storage state each time we change project.
        this.projectStore.projectId$.pipe(distinctUntilChanged()).subscribe((projectId) => {
            treeObservableService.clearLastSelectedNode();
            this.hybridCommunicationService.sendClearOldSiteStateStorage();
        });

        routerEventsFacade.hookRouterEvents();

        sessionRepository.applicationConfig$
            .pipe(
                switchMap((appConfig) => {
                    if (!appConfig) return of(appConfig);
                    const lang = appConfig.language;
                    return (lang ? translate.use(lang) : translate.use('en_US')).pipe(map(() => appConfig));
                })
            )
            .subscribe((appConfig) => {
                if (!appConfig) {
                    hybridCommunicationService.sendLogout();
                    router.navigate(['login']);
                }

                this.vm = { ...this.vm, isLoggedIn: !!appConfig };
                this.vm$.next(this.vm);
                if (appConfig?.pendoKey) {
                    init(appConfig.pendoKey);
                    pendoService.initPendo(appConfig, persistence.getLastProjectInfo());
                }
            });
        translate.onLangChange.pipe(take(1)).subscribe(() => {
            recommendationService.showRecommendations();
        });

        hybridMessagePayloadService.title$
            .pipe(switchMap((title) => translate.get('General.title', { title })))
            .subscribe((title) => titleService.setTitle(title));
    }

    private listenToAppToggle() {
        this.hybridIframeManager.showNewApp$.subscribe((v) => {
            this.vm = { ...this.vm, showNewApp: v };
            this.vm$.next(this.vm);
        });
    }
}
